import React, { useEffect, useState, useRef } from 'react';
import { Avatar, Box, IconButton, CircularProgress, Menu, MenuItem, ListItemIcon, ListItemText, Dialog } from '@mui/material';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonIcon from '@mui/icons-material/Person';
import CloseIcon from '@mui/icons-material/Close';
import { downloadPatientPhoto, uploadPatientPhoto, deletePatientPhoto } from '../services/api';

// La foto requiere JWT, así que no se puede usar <img src="/patients/{id}/photo">
// directo — se trae el blob autenticado y se resuelve a una object URL local
// (mismo patrón que DecisionMedia.js).
const PatientAvatar = ({ patient, size = 40, editable = false, onChanged }) => {
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [viewerOpen, setViewerOpen] = useState(false);
  const fileInputRef = useRef(null);
  const hasPhoto = !!patient?.has_photo;

  useEffect(() => {
    let objectUrl = null;
    let cancelled = false;
    setUrl(null);
    if (hasPhoto && patient?.id) {
      downloadPatientPhoto(patient.id)
        .then((res) => {
          if (cancelled) return;
          objectUrl = URL.createObjectURL(res.data);
          setUrl(objectUrl);
        })
        .catch(() => {});
    }
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [patient?.id, hasPhoto]);

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setLoading(true);
    try {
      await uploadPatientPhoto(patient.id, file);
      onChanged && onChanged();
    } catch {
      // el flujo de error global (interceptor) ya notifica
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setMenuAnchor(null);
    setLoading(true);
    try {
      await deletePatientPhoto(patient.id);
      onChanged && onChanged();
    } catch {
    } finally {
      setLoading(false);
    }
  };

  const avatarNode = (
    <Avatar
      src={url || undefined}
      onClick={hasPhoto ? () => setViewerOpen(true) : undefined}
      sx={{
        width: size, height: size, fontSize: size * 0.45,
        cursor: hasPhoto ? 'pointer' : 'default',
      }}
    >
      {!url && (patient?.name ? patient.name.charAt(0).toUpperCase() : <PersonIcon />)}
    </Avatar>
  );

  const viewerDialog = hasPhoto && (
    <Dialog open={viewerOpen} onClose={() => setViewerOpen(false)} maxWidth="md">
      <Box sx={{ position: 'relative', bgcolor: 'common.black', lineHeight: 0 }}>
        <IconButton
          onClick={() => setViewerOpen(false)}
          sx={{ position: 'absolute', top: 8, right: 8, bgcolor: 'rgba(0,0,0,0.5)', color: 'white', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}
        >
          <CloseIcon />
        </IconButton>
        {url && (
          <Box component="img" src={url} alt={patient?.name || 'Foto del paciente'}
            sx={{ display: 'block', maxWidth: '90vw', maxHeight: '85vh', width: 'auto', height: 'auto' }} />
        )}
      </Box>
    </Dialog>
  );

  if (!editable) {
    return (
      <>
        {avatarNode}
        {viewerDialog}
      </>
    );
  }

  return (
    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
      {avatarNode}
      {loading && (
        <CircularProgress
          size={size}
          sx={{ position: 'absolute', top: 0, left: 0 }}
        />
      )}
      <IconButton
        size="small"
        onClick={(e) => setMenuAnchor(e.currentTarget)}
        sx={{
          position: 'absolute', bottom: -4, right: -4,
          bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider',
          width: 24, height: 24,
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <PhotoCameraIcon sx={{ fontSize: 14 }} />
      </IconButton>

      {/* Sin `capture`: deja que el navegador ofrezca elegir cámara o
          galería, como pidió el usuario explícitamente (no cámara directa). */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        hidden
        onChange={handleFileSelected}
      />

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        <MenuItem dense onClick={() => { setMenuAnchor(null); fileInputRef.current?.click(); }}>
          <ListItemIcon><PhotoCameraIcon fontSize="small" /></ListItemIcon>
          <ListItemText>{hasPhoto ? 'Cambiar foto' : 'Agregar foto'}</ListItemText>
        </MenuItem>
        {hasPhoto && (
          <MenuItem dense onClick={handleDelete} sx={{ color: 'error.main' }}>
            <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
            <ListItemText>Quitar foto</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {viewerDialog}
    </Box>
  );
};

export default PatientAvatar;
